import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { Button } from "ui/Button";
import { Drawer, DrawerContent } from "ui/Drawer";
import { Sheet, SheetContent } from "ui/Sheet";
import { getShareUiStrings, resolveShareLocale } from "i18n/shareUi";
import type { SharedEntityMeta } from "types/chat";
import { useUser } from "util/UserContext";
import { getSharedEntityCanonicalUrl, getSharedEntityKindLabel } from "util/sharedEntity";

interface ContentViewerOverlayProps {
  sharedEntity: SharedEntityMeta | null;
  isOpen: boolean;
  onClose: () => void;
}

const ContentViewerOverlay: React.FC<ContentViewerOverlayProps> = ({
  sharedEntity,
  isOpen,
  onClose,
}) => {
  const { userIsMobile } = useUser();
  const ui = getShareUiStrings(resolveShareLocale());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const canonicalUrl = useMemo(
    () => (sharedEntity ? getSharedEntityCanonicalUrl(sharedEntity) : ""),
    [sharedEntity]
  );
  const kindLabel = sharedEntity
    ? getSharedEntityKindLabel(sharedEntity.entity_type)
    : ui.sharedContentLabel;

  useEffect(() => {
    setIsLoading(Boolean(canonicalUrl));
  }, [canonicalUrl]);

  const content = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-eoe-border-main px-4 py-3 dark:border-eoe-dark-border-main">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {kindLabel}
          </div>
          <div className="truncate text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
            {sharedEntity?.title || ui.sharedContentLabel}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (canonicalUrl) {
                window.open(canonicalUrl, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <ExternalLink className="size-4" />
            {ui.openInNewTab}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={ui.closeViewer}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="relative flex-1 bg-eoe-bg dark:bg-eoe-dark-bg">
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-eoe-bg/70 text-sm text-eoe-text-sub dark:bg-eoe-dark-bg/70 dark:text-eoe-dark-text-sub">
            {ui.loadingContent}
          </div>
        ) : null}
        {canonicalUrl ? (
          <iframe
            key={canonicalUrl}
            title={sharedEntity?.title || ui.sharedContentLabel}
            src={canonicalUrl}
            className="h-full w-full border-0"
            onLoad={() => setIsLoading(false)}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.contentUnavailable}
          </div>
        )}
      </div>
    </div>
  );

  if (!sharedEntity) {
    return null;
  }

  if (userIsMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsLoading(true);
            onClose();
          }
        }}
      >
        <DrawerContent fullScreen className="!rounded-none !border-none p-0" hideHandle>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsLoading(true);
          onClose();
        }
      }}
    >
      <SheetContent side="right" className="w-full p-0 sm:max-w-[72vw]">
        {content}
      </SheetContent>
    </Sheet>
  );
};

export default ContentViewerOverlay;
